import React, { useCallback, useState, useRef } from "react";
import {
  Card,
  LoadingOverlay,
  ActionIcon,
  Button,
  Text,
  Box,
  createStyles,
} from "@mantine/core";
import { Refresh, FaceId, Camera, Upload } from "tabler-icons-react";
import { useDropzone } from "react-dropzone";

const brandColor = "rgb(42, 155, 73)";

const getColor = ({ isDragAccept, isDragReject, isFocused }) => {
  if (isDragAccept) return brandColor;
  if (isDragReject) return "#ff5252";
  if (isFocused) return "rgba(42, 155, 73, 0.7)";
  return "#ced4da";
};

const useStyles = createStyles((theme) => ({
  wrapper: { width: "100%", height: "100%" },
  card: {
    backgroundColor: theme.colorScheme === "dark" ? theme.colors.dark[7] : theme.white,
    border: `1px solid ${theme.colorScheme === "dark" ? theme.colors.dark[5] : "#e9ecef"}`,
    borderRadius: theme.radius.lg,
    padding: theme.spacing.xl,
    boxShadow: "none",
    height: "100%",
  },
  dropzone: {
    borderRadius: theme.radius.lg,
    border: `1px dashed ${theme.colorScheme === "dark" ? theme.colors.dark[4] : "#ced4da"}`,
    padding: theme.spacing.xl,
    backgroundColor: theme.colorScheme === "dark" ? theme.colors.dark[6] : "#f8f9fa",
    textAlign: "center",
    cursor: "pointer",
    transition: "all 0.2s ease",
    "&:hover": { borderColor: brandColor, backgroundColor: "rgba(42,155,73,0.05)" },
  },
  previewBox: {
    width: "100%",
    height: 400,
    position: "relative",
    borderRadius: theme.radius.lg,
    overflow: "hidden",
    border: `1px solid ${theme.colorScheme === "dark" ? theme.colors.dark[5] : "#e9ecef"}`,
    backgroundColor: theme.colorScheme === "dark" ? theme.colors.dark[6] : "#f8f9fa",
  },
  emptyState: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 16,
    color: theme.colorScheme === "dark" ? theme.colors.dark[2] : "#868e96",
  },
  startButton: {
    backgroundColor: "rgba(42,155,73,0.1)",
    color: brandColor,
    border: "none",
    borderRadius: theme.radius.xl,
    padding: "14px 28px",
    fontWeight: 600,
    transition: "all 0.2s ease",
    "&:hover": { backgroundColor: "rgba(42,155,73,0.15)", transform: "translateY(-1px)" },
    "&:active": { transform: "translateY(0)" },
  },
  actionIcon: {
    backgroundColor: "rgba(42,155,73,0.1)",
    color: brandColor,
    border: "none",
    "&:hover": { backgroundColor: "rgba(42,155,73,0.15)" },
  },
  primaryButton: {
    backgroundColor: brandColor,
    color: theme.white,
    borderRadius: theme.radius.md,
    fontWeight: 500,
    border: "none",
    transition: "all 0.2s ease",
    "&:hover": { backgroundColor: "rgb(35,135,60)", transform: "translateY(-1px)" },
    "&:active": { transform: "translateY(0)" },
  },
  secondaryButton: {
    backgroundColor: "transparent",
    color: brandColor,
    border: `1px solid rgba(42,155,73,0.2)`,
    borderRadius: theme.radius.md,
    fontWeight: 500,
    transition: "all 0.2s ease",
    "&:hover": { backgroundColor: "rgba(42,155,73,0.05)", transform: "translateY(-1px)" },
    "&:active": { transform: "translateY(0)" },
  },
  faceOverlay: {
    position: "absolute",
    cursor: "pointer",
    borderRadius: theme.radius.sm,
    border: "2px solid rgba(255,255,255,0.7)",
    transition: "all 0.2s ease",
    "&:hover": { borderColor: brandColor, backgroundColor: "rgba(42,155,73,0.08)" },
  },
}));

const DetectionCard = ({
  toggle,
  setToggle,
  setCanDetecae,
  setFaceImage,
  startVideo,
  isLoading,
  customLoader,
  onPlay,
  videoRef,
  canvasRef,
  faceImage,
  snapPhoto,
  stopCamera,
  captureImage,
}) => {
  const [pickedImage, setPickedImage] = useState(null);
  const pickedImageRef = useRef(null);
  const [facesInPickedImage, setFacesInPickedImage] = useState([]);
  const [selectedFaceInImageIndex, setSelectedFaceInImageIndex] = useState(-1);
  const [facesInPickedImageOrgianl, setFacesInPickedImageOrginal] = useState([]);

  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    const reader = new FileReader();
    reader.onabort = () => console.log("file reading was aborted");
    reader.onerror = () => console.log("file reading has failed");
    reader.onload = () => {
      setPickedImage(reader.result);
      setFaceImage(reader.result);
    };
    reader.readAsDataURL(file);
  }, [setFaceImage]);

  const setSelectedSignelFaceFromImage = useCallback((index) => {
    setSelectedFaceInImageIndex(index);
    const face = facesInPickedImageOrgianl[index];
    const canvas = document.createElement("canvas");
    canvas.width = face.box.width;
    canvas.height = face.box.height;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(
      pickedImageRef.current,
      face.box.x,
      face.box.y,
      face.box.width,
      face.box.height,
      0,
      0,
      face.box.width,
      face.box.height
    );
    setFaceImage(canvas.toDataURL("image/jpeg"));
  }, [pickedImageRef, facesInPickedImageOrgianl]);

  const { getRootProps, getInputProps, isFocused, isDragAccept, isDragReject } =
    useDropzone({ accept: { "image/*": [] }, onDrop });

  const { classes } = useStyles();

  const rootProps = getRootProps();

  return (
    <div className={classes.wrapper}>
      <Card className={classes.card}>
        <Box
          {...rootProps}
          className={classes.dropzone}
          sx={{
            borderColor: getColor({ isFocused, isDragAccept, isDragReject }),
          }}
        >
          <input {...getInputProps()} />
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
            <Upload size={30} color={brandColor} />
            <Text align="center" size="md" color="dimmed">
              اسحب الصورة داخل هذا الصندوق او اضغط لتحديد الصورة
            </Text>
          </Box>
        </Box>

        <Box mt="lg" sx={{ position: "relative" }}>
          {isLoading && (
            <LoadingOverlay
              visible
              loader={customLoader}
              overlayBlur={0}
              overlayOpacity={0.2}
            />
          )}

          {!toggle && !pickedImage ? (
            <Box className={classes.previewBox}>
              <Box className={classes.emptyState}>
                <FaceId size={48} color={brandColor} opacity={0.7} />
                <Text align="center" size="lg" weight={500}>
                  اضغط لبدء التقاط الصورة
                </Text>
                <Button
                  className={classes.startButton}
                  leftIcon={<Camera size={18} />}
                  onClick={() => {
                    setToggle(true);
                    setCanDetecae(true);
                    setFaceImage(null);
                    startVideo();
                  }}
                >
                  تشغيل الكاميرا
                </Button>
              </Box>
            </Box>
          ) : (
            <Box className={classes.previewBox}>
              {!pickedImage ? (
                <>
                  <video
                    id="video"
                    width="100%"
                    height="400"
                    autoPlay
                    muted
                    onPlay={onPlay}
                    ref={videoRef}
                    style={{ objectFit: "cover", display: "block" }}
                  />
                  <canvas
                    ref={canvasRef}
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                    }}
                  />
                </>
              ) : (
                <div style={{ position: "relative", width: "100%", height: "100%" }}>
                  <img
                    src={pickedImage}
                    ref={pickedImageRef}
                    alt="Selected"
                    style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                  />
                  <div style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%" }}>
                    {facesInPickedImage.map((face, index) => (
                      <Box
                        key={`face-img-${index}`}
                        onClick={() => setSelectedSignelFaceFromImage(index)}
                        className={classes.faceOverlay}
                        sx={{
                          width: face.box._width + "px",
                          height: face.box._height + "px",
                          top: face.box._y + "px",
                          left: face.box._x + "px",
                          backgroundColor:
                            index === selectedFaceInImageIndex
                              ? "rgba(42, 155, 73, 0.2)"
                              : "rgba(255,255,255,0)",
                          borderColor:
                            index === selectedFaceInImageIndex ? brandColor : "rgba(255,255,255,0.7)",
                        }}
                      />
                    ))}
                  </div>
                </div>
              )}

              {faceImage && !pickedImage && (
                <Box sx={{ position: "absolute", top: 12, right: 12, zIndex: 10 }}>
                  <ActionIcon
                    className={classes.actionIcon}
                    size="lg"
                    radius="xl"
                    onClick={() => {
                      setFaceImage(null);
                      setCanDetecae(true);
                      startVideo();
                    }}
                  >
                    <Refresh size={20} />
                  </ActionIcon>
                </Box>
              )}
            </Box>
          )}

          <Box mt="lg" sx={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Button
              className={classes.primaryButton}
              size="md"
              fullWidth
              onClick={() => {
                if (faceImage) snapPhoto();
                else captureImage();
              }}
              leftIcon={faceImage ? <FaceId size={20} /> : <Camera size={20} />}
            >
              {faceImage ? "التعرف على الوجه" : "التقط الصورة"}
            </Button>
            <Button
              className={classes.secondaryButton}
              size="md"
              fullWidth
              onClick={() => {
                if (pickedImage) {
                  setPickedImage(null);
                  setFaceImage(null);
                } else {
                  setCanDetecae(false);
                  setFaceImage(null);
                  stopCamera();
                  setToggle(false);
                }
              }}
              leftIcon={<Refresh size={20} />}
            >
              {pickedImage ? "حدد الصورة بواسطة الكاميرة" : "اعادة"}
            </Button>
          </Box>
        </Box>
      </Card>
    </div>
  );
};

export default DetectionCard;
